/*
 * c-anchor-scroll — makes same-page anchor links (and initial deep links) work on the
 * desktop "squeeze" layout.
 *
 * On desktop (>=990px) Horizon scrolls `.page-wrapper` while html/body are
 * overflow:hidden, so a native <a href="#id"> click only updates the URL and never
 * moves the page (dosage-guide/panel "#subscribe" CTAs, roots-good-years, the
 * skip-to-content links, etc. all land nowhere), and an initial /path#section deep link
 * mis-lands because the browser scrolls the overflow:hidden document, not .page-wrapper.
 * This installs ONE delegated click handler that scrolls the correct container for any
 * same-page hash link whose target exists, and corrects the initial hash on load. The
 * handler prevents native fragment navigation (and with it native focus movement), so
 * it moves focus itself when the scroll lands — land-gated and intent-guarded, see
 * focusTarget / trackFocusIntent in @theme/c-anchor-utils. Skip links keep their
 * native semantics on BOTH paths — a skip-link click and an initial URL naming a
 * skip destination focus the target container DIRECTLY (target-first) — while
 * content anchors go through the focus pick.
 *
 * Deferral: the click handler bails on `e.defaultPrevented`, so handlers that already
 * claimed the click keep full control — notably the PDP anchor nav (c-pdp-anchor-nav),
 * whose own listener sits on a closer ancestor and runs first. Never double-scrolls.
 *
 * The animation is the shared single-owner tween in @theme/c-scroll-tween — the SAME
 * owner the anchor nav uses — so a scroll started here and one started by the nav can
 * never race to different destinations: the later action always supersedes the earlier.
 *
 * Known open limit: this handler has no scroll-spy, so after a mid-flight user takeover
 * it does not reconcile the URL hash to the section actually reached (the hash is written
 * only on a completed landing).
 */
import { getScrollContainer } from '@theme/scroll-container';
import { tweenScrollTo } from '@theme/c-scroll-tween';
import { targetsThisPage, focusTarget, trackFocusIntent } from '@theme/c-anchor-utils';

/* Resolve "#id" to its element, guarding malformed percent-encoding (which would throw
   and abort the rest of the module). */
function resolveHashTarget(hash) {
  if (!hash || hash === '#') return null;
  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch (_) {
    return null;
  }
}

/* Absolute container scrollTop that lands `el` at its own scroll-margin-top line — the
   SAME line the anchor nav's scroll-spy reads, so the landing and the active underline
   always agree (this is what native fragment scroll gets wrong on the squeeze layout). */
function landingTop(container, el) {
  const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  return container.scrollTop + el.getBoundingClientRect().top - margin;
}

/* focusTarget / targetsThisPage / trackFocusIntent live in @theme/c-anchor-utils,
   shared with the PDP anchor nav's handler so the two cannot drift. */

document.addEventListener('click', (e) => {
  // Let already-handled clicks and open-in-new-tab/modified clicks fall through.
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

  const link = e.target.closest('a[href^="#"]');
  if (!link) return;
  // Let the browser navigate / open / download natively when the link doesn't
  // resolve to this page — otherwise preventDefault would hijack a real
  // cross-context navigation into an in-place scroll.
  if (!targetsThisPage(link) || link.hasAttribute('download')) return;
  const hash = link.getAttribute('href');
  const el = resolveHashTarget(hash);
  if (!el) return; // unknown / malformed target: don't hijack the click

  e.preventDefault();

  const container = getScrollContainer();
  /* Write the URL — and move focus — only when the scroll actually LANDS (onComplete),
     never upfront: if the user takes over mid-scroll the tween yields WITHOUT onComplete,
     so a taken-over jump leaves the URL clean instead of lying about a section you didn't
     end on, and focus isn't stranded on a destination thousands of pixels offscreen that
     the user just cancelled. Focus additionally moves only if the user hasn't EXPLICITLY
     refocused since the click (focusin-tracked — see trackFocusIntent): a mid-tween Tab
     keeps user control instead of being stolen ~420ms later, while involuntary focus
     loss (the clicked control hidden mid-scroll, focus dumped on <body>) does NOT cancel
     the transfer. replaceState (not pushState): keeps Horizon's per-entry scroll
     restoration intact (scroll-container.js stores scrollTop in history.state) and
     avoids a history entry per jump. */
  /* Skip links (all four render via snippets/skip-to-content-link.liquid) get
     target-first focus: their whole purpose is to put focus AT the container
     (#MainContent / #ProductInformation-… / #ResultsList), so the picker must
     not redirect it to a receiver/heading nested inside — that would bypass
     everything the skip link exists to reach first. */
  const skipLink = link.classList.contains('skip-to-content-link');
  const intent = trackFocusIntent();
  tweenScrollTo(container, () => landingTop(container, el), {
    onComplete: () => {
      history.replaceState(history.state, '', hash);
      intent.stop();
      if (!intent.userMovedFocus) focusTarget(el, { direct: skipLink });
    },
    onYield: () => intent.stop(),
    onSupersede: () => intent.stop(),
  });
});

/* Land the deep-link target at its live scroll-margin line (the line the anchor nav's
   scroll-spy reads), overriding the browser's native fragment scroll which mis-lands
   against the .page-wrapper scroller. Instant, matching normal deep-link behavior.
   Returns the target element and how far this frame had to move it (0 == already on its
   line, i.e. layout stable), or null if there's no valid target. */
function landOnInitialHash(container) {
  const el = resolveHashTarget(location.hash);
  if (!el || !container) return null;
  const before = container.scrollTop;
  const max = Math.max(0, container.scrollHeight - container.clientHeight);
  const target = Math.max(0, Math.min(landingTop(container, el), max));
  container.scrollTo({ top: target, behavior: 'instant' });
  return { el, corrected: Math.abs(target - before) };
}

if (location.hash) {
  /* A single early land isn't enough: late images / fonts / the sticky nav's final height
     shift the target out from under it (observed ~16px, enough to disagree with the spy),
     and that shift can arrive hundreds of ms after first paint. So re-land each frame and
     only STOP once the target has needed no correction for several consecutive frames —
     i.e. layout has actually stopped moving it — with a hard cap as a backstop. Stop
     immediately if the user takes over; detect that from REAL input (wheel / touch / key /
     pointer), not scrollTop divergence, which scroll-anchoring trips on its own while
     images load. Re-landing to an unchanged position is a no-op, so a stable page isn't
     "fought" — the loop settles within a few frames of the last shift. */
  const inputAC = new AbortController();
  let userTookOver = false;
  let watcher = null;
  /* <html data-roots-deep-link> marks this window so the anchor nav holds its URL sync
     (c-pdp-anchor-nav.js) until the landing is settled or the reader takes over. */
  document.documentElement.setAttribute('data-roots-deep-link', '');
  const stop = () => { inputAC.abort(); watcher?.disconnect(); document.documentElement.removeAttribute('data-roots-deep-link'); };
  const markUser = () => { userTookOver = true; stop(); };
  for (const ev of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
    addEventListener(ev, markUser, { passive: true, signal: inputAC.signal });
  }

  let landed = null; // { el, direct }: the target and how to focus it
  // Re-resolve: late hydration can replace the target node after the first land.
  const focusLanded = () => focusTarget(resolveHashTarget(location.hash) || landed.el, { direct: landed.direct });

  /* After the loop settles, a shift can still arrive seconds later on a slow connection:
     Judge.me swapping its reviews (the page shrinks under the target, clamping the scroll,
     then grows back), a late section script. Each one changes the size of the header or of
     <main>, so a ResizeObserver on those re-lands the target, in the same frame, before
     paint. Nothing polls and a quiet page does nothing. The same window re-asserts focus:
     on a cold load the browser dropped it to <body> about 2s after landing (no script blur;
     the focused node itself stayed connected and visible), and only real input may take it.
     Ends on that input, or 10s after load. ponytail: fixed window; a reflow later than
     that is left to the browser's own scroll anchoring. */
  const watch = () => {
    watcher = new ResizeObserver(() => { if (!userTookOver) landOnInitialHash(getScrollContainer()); });
    for (const el of [document.querySelector('main'), ...(document.getElementById('header-group')?.children || [])]) {
      if (el) watcher.observe(el);
    }
    const end = () => setTimeout(stop, 10000);
    if (document.readyState === 'complete') end();
    else addEventListener('load', end, { once: true, signal: inputAC.signal });
  };

  let stableFrames = 0;
  let totalFrames = 0;
  const settle = () => {
    if (userTookOver) return;
    const res = landOnInitialHash(getScrollContainer());
    if (!res) { stop(); return; }
    if (!landed) {
      /* Deep links naming a skip-link DESTINATION get the same target-first focus as a
         skip-link click (a skip landing writes its hash via replaceState, so reloading /
         sharing that URL arrives here): running the picker on #MainContent would land
         focus on whatever receiver/heading sits inside <main>. Detect by resolving every
         skip link's own href and comparing elements — not raw hash strings, which can
         disagree on percent-encoding — so the set of skip destinations comes from the
         page itself (snippets/skip-to-content-link.liquid), never a hardcoded id list. */
      const direct = [...document.querySelectorAll('a.skip-to-content-link[href^="#"]')].some(
        (link) => resolveHashTarget(link.getAttribute('href')) === res.el,
      );
      landed = { el: res.el, direct };
      focusLanded(); // focus on the first land, then keep it there until input or the window ends
      addEventListener('focusout', (e) => {
        if (e.relatedTarget) return; // focus moved to a real element: not ours to undo
        requestAnimationFrame(() => {
          if (!userTookOver && (!document.activeElement || document.activeElement === document.body)) focusLanded();
        });
      }, { capture: true, signal: inputAC.signal });
    }
    stableFrames = res.corrected < 1 ? stableFrames + 1 : 0;
    // hand over to the watcher once the line has held for ~8 frames, or after a ~3s backstop
    if (stableFrames < 8 && ++totalFrames < 180) requestAnimationFrame(settle);
    else watch();
  };
  requestAnimationFrame(settle);
}
